import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE from "../config/api";

export default function MyListings({ onUpgrade }) {
    const navigate = useNavigate();
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);

    const userData = JSON.parse(localStorage.getItem("user"));
    const user_id = userData?.personaluid;
    const ITEMS_PER_PAGE = 10;

    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        if (!user_id) {
            setLoading(false);
            return;
        }

        fetch(`${API_BASE}/api/get_user_listings.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ user_id }),
        })
            .then(res => res.json())
            .then(data => {
                if (data.success) setListings(data.listings);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [user_id]);

    const openListing = (listinguid) => navigate(`/profile?listinguid=${listinguid}`);

    const goTocommandrev = (listinguid) => navigate(`/commentsreviews?listinguid=${listinguid}`);
    useEffect(() => {
        // Initialize tooltips
        const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        const tooltipList = [...tooltipTriggerList].map(
            el => new window.bootstrap.Tooltip(el)
        );

        // Cleanup: Destroy tooltips when component unmounts
        return () => {
            tooltipList.forEach(t => t.dispose());
        };
    }, [listings]);

    // 🔍 SEARCH FILTER
    const filteredListings = listings.filter(item =>
        item.title.toLowerCase().includes(search.toLowerCase()) ||
        item.email.toLowerCase().includes(search.toLowerCase()) ||
        item.mobile.includes(search)
    );

    // 📄 PAGINATION
    const totalPages = Math.ceil(filteredListings.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginatedListings = filteredListings.slice(
        startIndex,
        startIndex + ITEMS_PER_PAGE
    );
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [currentPage]);

    // BEAUTIFUL LOADER
    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "70vh" }}>
                <div className="spinner-box text-center">
                    <div className="pulse-loader"></div>
                    <p className="mt-3 text-secondary fw-semibold">Fetching your business listings...</p>
                </div>

                <style>
                    {`
                        .pulse-loader {
                            width: 60px;
                            height: 60px;
                            border-radius: 50%;
                            background: linear-gradient(135deg, #4a90e2, #5fb3ff);
                            animation: pulse 1.2s infinite ease-in-out;
                        }
                        @keyframes pulse {
                            0% { transform: scale(0.85); opacity: 0.7; }
                            50% { transform: scale(1); opacity: 1; }
                            100% { transform: scale(0.85); opacity: 0.7; }
                        }
                    `}
                </style>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">

            {/* PAGE HEADER */}
            <div
                className="rounded p-3 mb-3 shadow-sm"
                style={{
                    background: "linear-gradient(135deg, #e8f0ff, #fefefe)",
                    borderLeft: "6px solid #4a90e2"
                }}
            >

                <h5 className="fw-bold text-dark mb-1">My Listed Businesses</h5>
                <p className="text-secondary mb-0">
                    View, manage and upgrade your business listings.
                </p>
            </div>


            {/* NO LISTINGS MESSAGE */}
            {listings.length === 0 ? (
                <div className="alert alert-info shadow-sm p-3 fs-5 text-center">
                    You have not listed any business yet.
                </div>
            ) : (
                <div className="table-responsive shadow rounded">
                    <div className="card shadow-sm border-0">

                        {/* TABLE CARD HEADER */}
                        <div className="card-header bg-white border-bottom">
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">

                                {/* 🔍 SEARCH */}
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Search business, email, phone..."
                                    value={search}
                                    onChange={(e) => {
                                        setSearch(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    style={{ width: "260px" }}
                                />

                                {/* 📄 PAGINATION */}
                                {totalPages > 1 && (
                                    <div className="btn-group btn-group-sm">
                                        <button
                                            className="btn btn-outline-primary"
                                            disabled={currentPage === 1}
                                            onClick={() => setCurrentPage(p => p - 1)}
                                        >
                                            ‹
                                        </button>

                                        {[...Array(totalPages)].map((_, i) => (
                                            <button
                                                key={i}
                                                className={`btn ${currentPage === i + 1
                                                    ? "btn-primary"
                                                    : "btn-outline-primary"
                                                    }`}
                                                onClick={() => setCurrentPage(i + 1)}
                                            >
                                                {i + 1}
                                            </button>
                                        ))}

                                        <button
                                            className="btn btn-outline-primary"
                                            disabled={currentPage === totalPages}
                                            onClick={() => setCurrentPage(p => p + 1)}
                                        >
                                            ›
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* TABLE CARD BODY */}
                        <div className="card-body p-0">

                            <div className="table-responsive">
                                <table className="table table-hover align-middle mb-0">
                                    <thead
                                        style={{
                                            background: "#f2f6ff",
                                            borderBottom: "2px solid #dbe5ff"
                                        }}
                                    >
                                        <tr>
                                            <th>#</th>
                                            <th>Business</th>
                                            <th>Location</th>
                                            <th>Contact</th>
                                            <th>Created</th>
                                            <th>Status</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {paginatedListings.map((item, index) => (
                                            <tr key={item.id} className="listing-row">

                                                <td className="fw-bold text-primary">
                                                    {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                                                </td>

                                                <td>
                                                    <h6 className="fw-semibold text-dark mb-1">{item.title}</h6>
                                                    <small className="text-muted">{item.email}</small>
                                                </td>

                                                <td className="text-muted">
                                                    📍 {item.province}, {item.district}
                                                </td>

                                                <td className="text-muted">
                                                    📞 {item.mobile}
                                                </td>

                                                <td className="text-muted small">{item.created_at}</td>

                                                <td>
                                                    {item.states == 1 ? (
                                                        <span className="badge bg-success">Approved</span>
                                                    ) : (
                                                        <span className="badge bg-warning text-dark">Pending</span>
                                                    )}
                                                </td>

                                                <td className="text-center">
                                                    <div className="d-flex justify-content-center gap-3">
                                                        <i
                                                            className="bi bi-eye-fill text-primary fs-5"
                                                            title="View"
                                                            onClick={() => openListing(item.listinguid)}
                                                            style={{ cursor: "pointer" }}
                                                        ></i>

                                                        <i
                                                            className="bi bi-star-fill text-warning fs-5"
                                                            title="Upgrade"
                                                            onClick={() => onUpgrade(item.listinguid)}
                                                            style={{ cursor: "pointer" }}
                                                        ></i>

                                                        <i
                                                            className="bi bi-chat-left-text-fill text-success fs-5"
                                                            title="Comments"
                                                            onClick={() => goTocommandrev(item.listinguid)}
                                                            style={{ cursor: "pointer" }}
                                                        ></i>
                                                    </div>
                                                </td>

                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                        </div>
                    </div>

                </div>
            )}

            {/* ROW HOVER EFFECT */}
            <style>
                {`
                    .listing-row:hover {
                        background: #f7faff !important;
                        cursor: pointer;
                        transition: 0.2s ease-in-out;
                    }

                    table {
                        border-radius: 12px !important;
                        overflow: hidden;
                    }
                `}
            </style>
        </div>
    );
}
